/**
 * MobileToday — RC8 Phase 1 Slice 3 · First-class mobile Today surface.
 *
 * PO-ratified 2026-02-16 · replaces the desktop `CruiseHome` reuse on
 * mobile with an intentional Today experience. Consumes the same
 * canonical composed read model:
 *
 *     GET /api/cruises/current/today   ·   returns `plan[]` composed
 *     from `cruise_dining_reservations`, `cruise_excursions`, real
 *     `journey_projections` travel segments, and voyage-derived events
 *     (Slice 1 + Slice 2). Every event carries a nested `time_context`
 *     of shape `{ instant_utc, context, instant_display }` per
 *     Decision F + ADR-TIME-001.
 *
 *     GET /api/ship-time/{cruise_id}   ·   authoritative four-clock
 *     envelope (UTC · ship · port · home) for the three-clock strip.
 *
 * The component MUST NOT compute a second time authority — every
 * displayed clock and instant derives from these two endpoints. Never
 * fabricate an event; if the composer returned `plan: []`, render the
 * empty state honestly.
 *
 * Non-goals in this slice (explicit deferrals per PO scope):
 *   · No SQLite offline layer (Slice N).
 *   · No collapsible multi-day view (that lives on the Voyage tab).
 *   · No dedicated countdown ticker on non-current events.
 *   · No Safety FAB redesign — the existing FAB persists across all tabs.
 *
 * Structural rule: the component consumes the canonical response as-is.
 * Adding an offline cache later just intercepts `fetchToday()` — no UI
 * rewrite needed.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Sun, Moon, Anchor, Home, MapPin, Utensils, Plane, Building2,
         Car, ParkingCircle, Compass, Info, AlertTriangle } from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
// RC8 Slice 6 · Offline foundation · read-through cache for the canonical
// composed `/today` projection. Same read-through pattern as MobileVoyage.
import { syncToday, readCachedToday } from "@/mobile/offline/sync";
// RC7 · M8 · What's Next / Ship Time Home hero. Sits at the top of the
// Today surface, additive (existing MobileToday content unchanged below).
import WhatsNextHero from "@/components/mobile-today/WhatsNextHero";
import ExploreShipModule from "@/components/ship-reference/ExploreShipModule";

// NOTE: The App-level route resolver wraps the returned element in the
// standard chrome (top strip · bottom tab bar · Safety FAB). MobileToday
// therefore renders the surface content only — no Shell wrapper here.

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F2244";
const GOLD = "#C28E42";

// Map event `kind` → icon. Never used to invent event data; purely
// decorative on real composed events.
const KIND_ICON = {
  dining:                     Utensils,
  excursion:                  MapPin,
  flight_booking:             Plane,
  hotel_reservation:          Building2,
  ground_reservation:         Car,
  transportation_reservation: Car,
  rental_car:                 Car,
  parking_reservation:        ParkingCircle,
  voyage_event:               Anchor,
};
const KIND_ICON_DEFAULT = Compass;

// Editorial label for a `time_context.context` value. Keeps the display
// honest without leaking `instant_utc` into presentation.
const CONTEXT_LABEL = {
  ship: "Ship Time",
  port: "Port Local",
  home: "Home Time",
  utc:  "UTC",
};

function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

function fmtDateEditorial(d = new Date()) {
  return d.toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });
}

/**
 * Select the next-upcoming event from a composed `plan[]` using the
 * canonical instant.  Past events remain visible in the timeline but
 * never win the "Next Event" slot. If no future instant exists, the
 * next-event card is omitted (never fabricated).
 */
function selectNextEvent(plan) {
  if (!Array.isArray(plan) || !plan.length) return null;
  const nowMs = Date.now();
  const futures = plan
    .map((e) => {
      const iso = e?.time_context?.instant_utc;
      if (!iso) return null;
      const t = Date.parse(iso);
      if (isNaN(t) || t < nowMs) return null;
      return { ...e, _ms: t };
    })
    .filter(Boolean)
    .sort((a, b) => a._ms - b._ms);
  return futures[0] || null;
}

function ClockCell({ label, iso, context, isPrimary }) {
  return (
    <div
      className={`flex-1 min-w-0 rounded-xl px-3 py-2 ${
        isPrimary ? "bg-white/10" : "bg-white/5"
      }`}
      data-testid={`mobile-today-clock-${context}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/70">
        {label}
      </div>
      <div className="text-lg font-semibold text-white leading-tight">
        {fmtClock(iso)}
      </div>
    </div>
  );
}

function EventRow({ event, isNext = false }) {
  const Icon = KIND_ICON[event.kind] || KIND_ICON_DEFAULT;
  const tc = event.time_context || {};
  const ctxLabel = CONTEXT_LABEL[tc.context] || "";
  return (
    <div
      className={`flex items-start gap-3 rounded-2xl p-4 ${
        isNext ? "bg-[#0F2244] text-white" : "bg-white shadow-sm border border-slate-100"
      }`}
      data-testid={`mobile-today-event-${event.id}`}
      data-event-kind={event.kind}
      data-event-context={tc.context || ""}
    >
      <div
        className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          isNext ? "bg-white/15 text-white" : "bg-slate-100 text-[#0F2244]"
        }`}
      >
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-xs uppercase tracking-wider ${
          isNext ? "text-white/70" : "text-slate-500"
        }`}>
          {tc.instant_display || event.time || "—"}
          {ctxLabel ? <span className="ml-2">· {ctxLabel}</span> : null}
        </div>
        <div className={`text-base font-semibold truncate ${
          isNext ? "text-white" : "text-slate-900"
        }`}>
          {event.title}
        </div>
        {event.location_from || event.location_to ? (
          <div className={`text-xs ${isNext ? "text-white/80" : "text-slate-500"}`}>
            {event.location_from}{event.location_to ? ` → ${event.location_to}` : ""}
          </div>
        ) : null}
        {event.confirmation_number ? (
          <div className={`text-[10px] mt-0.5 ${isNext ? "text-white/60" : "text-slate-400"}`}>
            Confirmation · {event.confirmation_number}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-white/60 backdrop-blur px-5 py-8 text-center"
      data-testid="mobile-today-empty-state"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-[#0F2244]">
        <Info size={22} />
      </div>
      <div className="mt-3 text-base font-semibold text-slate-900">
        Nothing scheduled today.
      </div>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">
        Cruise Command will surface your confirmed travel, dining,
        excursions and voyage events here — the moment they exist.
      </p>
    </div>
  );
}

export default function MobileToday() {
  const { cruise, today: contextToday, voyageMeta, setRoute } = useCruise();
  const [today, setToday] = useState(contextToday || null);
  const [shipTime, setShipTime] = useState(null);
  const [loading, setLoading] = useState(!contextToday);
  const [err, setErr] = useState(null);

  useEffect(() => { setToday(contextToday || null); }, [contextToday]);

  // Ship-time envelope refresh · canonical four-clock endpoint.
  useEffect(() => {
    if (!cruise?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/ship-time/${encodeURIComponent(cruise.id)}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(`ship-time ${r.status}`);
        const j = await r.json();
        if (!cancelled) setShipTime(j);
      } catch (e) {
        if (!cancelled) setErr(String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [cruise?.id]);

  // Foreground refresh of composed /today if context has nothing.
  // RC8 Slice 6 · reads through the offline cache so a re-launch in
  // low-connectivity gets a fast cached paint, refreshed from network
  // on top when available. Backend remains authoritative.
  useEffect(() => {
    if (contextToday) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // 1) Cache-first paint (if scopes known).
        if (cruise?.owner_id && cruise?.id) {
          const cached = await readCachedToday({
            user_id: cruise.owner_id, voyage_id: cruise.id,
          });
          if (cached && !cancelled) {
            setToday(cached);
            setLoading(false);
          }
        }
        // 2) Network refresh + persist.
        const { data, meta } = await syncToday({
          user_id: cruise?.owner_id || null,
          voyage_id: cruise?.id || null,
        });
        if (cancelled) return;
        if (data) {
          setToday(data);
        } else if (meta.source === "empty") {
          setErr(meta.error || "offline_no_cache");
        }
      } catch (e) {
        if (!cancelled) setErr(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contextToday, cruise?.owner_id, cruise?.id]);

  const plan = today?.plan || [];
  const nextEvent = useMemo(() => selectNextEvent(plan), [plan]);
  const shipName = cruise?.ship_name || today?.voyage?.ship_name || "";
  const dayLabel = today?.day_label || "";
  // RC7-M8 · Connectivity mirror for the WhatsNextHero offline chip.
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-28"
      data-testid="mobile-today-root"
    >
        {/* RC7-M8 · What's Next / Ship Time Home hero. Additive: sits
            ABOVE the existing Today header. When offline, the offline
            chip is rendered on the hero and the underlying `today`
            payload is served from the M4 offline cache via readCachedToday. */}
        <WhatsNextHero today={today} shipTime={shipTime} online={online} />
        {/* Editorial header */}
        <div
          className="px-5 pt-6 pb-5 text-white rounded-b-3xl"
          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #14315e 100%)` }}
          data-testid="mobile-today-header"
        >
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">
            Today
          </div>
          <h1
            className="mt-1 font-display font-black tracking-tight text-white"
            style={{ fontSize: "clamp(22px, 5.6vw, 28px)", lineHeight: 1.05 }}
            data-testid="mobile-today-title"
          >
            {shipName || "Your voyage"}
          </h1>
          <div className="mt-1 text-sm text-white/80">
            {fmtDateEditorial()} {dayLabel ? `· ${dayLabel}` : ""}
          </div>

          {/* Three-clock strip · authoritative envelope only */}
          <div className="mt-4 flex gap-2" data-testid="mobile-today-clock-strip">
            <ClockCell label="Ship" iso={shipTime?.ship} context="ship" isPrimary />
            <ClockCell label="Port" iso={shipTime?.port} context="port" />
            <ClockCell label="Home" iso={shipTime?.home} context="home" />
          </div>
        </div>

        {/* Next event card */}
        {nextEvent ? (
          <div className="px-5 mt-5" data-testid="mobile-today-next-event">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
              Next up
            </div>
            <EventRow event={nextEvent} isNext />
          </div>
        ) : null}

        {/* Today's itinerary */}
        <div className="px-5 mt-6" data-testid="mobile-today-itinerary">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Today's itinerary
          </div>
          {loading ? (
            <div className="rounded-2xl bg-white p-4 text-sm text-slate-500 border border-slate-100">
              Loading…
            </div>
          ) : err ? (
            <div
              className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 border border-red-100 flex items-start gap-2"
              data-testid="mobile-today-error"
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>Couldn't load your day. Pull to refresh.</div>
            </div>
          ) : plan.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-2">
              {plan.map((e) => (
                <EventRow key={e.id} event={e} isNext={nextEvent && e.id === nextEvent.id} />
              ))}
            </div>
          )}
        </div>

        {/* RC8 · Compact "Explore ship" module. Opens the full-screen Ship
            Guide via its CTA — the complete catalogue never renders here. */}
        <div className="px-5 mt-6" data-testid="mobile-today-explore-ship">
          <ExploreShipModule
            shipId={voyageMeta?.ship_id}
            shipName={voyageMeta?.ship_name || ""}
            onOpenGuide={(tab) => setRoute(tab ? `ship-guide:${tab}` : "ship-guide")}
          />
        </div>
      </div>
  );
}

// Named export for testing helpers.
export { selectNextEvent };
